// src/components/BetaStats/BetaStats.jsx
import React, { useEffect, useState } from 'react';
import './BetaStats.css';
import BetaMap from './BetaMap';

const STATS_ENDPOINT = '/api/beta-stats';

// Shown only on the local dev server, where /api isn't served by `npm start`, so the design is previewable.
// Production builds never use this: if the live data can't load, the banner simply doesn't render.
const DEV_SAMPLE = {
  dancers: 24,
  fitters: 5,
  shoes: 112,
  snapshots: 840,
  locations: [
    { city: 'Baltimore', region: 'MD', lat: 39.3, lng: -76.6, home: true },
    { city: 'Charlottesville', region: 'VA', lat: 38.0, lng: -78.5 },
    { city: 'Manassas', region: 'VA', lat: 38.8, lng: -77.5 },
  ],
  testingStarted: '2026-09-20',
  lastTest: '2026-10-14',
  topProblems: [
    { label: 'Box Too Wide', count: 31 },
    { label: 'Vamp Too Long', count: 22 },
    { label: 'Shank Too Soft', count: 17 },
    { label: 'Box Too Narrow', count: 12 },
    { label: 'Crown Too High', count: 8 },
  ],
};

const isValidStats = (d) =>
  d &&
  Number.isFinite(d.dancers) &&
  Number.isFinite(d.fitters) &&
  Number.isFinite(d.shoes) &&
  Array.isArray(d.topProblems);

// Dates arrive as YYYY-MM-DD. Format them in UTC so a timezone can never shift the day.
const formatDate = (iso) => {
  if (!iso) return 'Not yet';
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

// "Los Angeles, CA" (region is optional).
const placeName = (p) => (p.region ? `${p.city}, ${p.region}` : p.city);

const BetaStats = () => {
  const [stats, setStats] = useState(null);
  const [isSample, setIsSample] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        const res = await fetch(STATS_ENDPOINT, { signal: controller.signal });
        if (!res.ok) throw new Error('stats unavailable');
        const data = await res.json();
        if (!isValidStats(data)) throw new Error('unexpected stats shape');
        setStats(data);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (process.env.NODE_ENV === 'development') {
          setStats(DEV_SAMPLE);
          setIsSample(true);
        }
      }
    };

    load();
    return () => controller.abort();
  }, []);

  if (!stats) return null;

  const maxCount = Math.max(1, ...stats.topProblems.map((p) => p.count));
  const locations = Array.isArray(stats.locations) ? stats.locations.filter((p) => p && p.city) : [];

  return (
    <section className="beta-stats" aria-label="Beta testing by the numbers">
      {isSample && <span className="beta-stats-sample">Sample data (dev only)</span>}

      <h3 className="beta-stats-title">Beta Testing by the Numbers</h3>

      <p className="beta-stats-dates">
        <span>
          Testing started: <strong>{formatDate(stats.testingStarted)}</strong>
        </span>
        <span className="beta-stats-sep" aria-hidden="true">&bull;</span>
        <span>
          Last test: <strong>{formatDate(stats.lastTest)}</strong>
        </span>
      </p>

      {locations.length > 0 && (
        <p className="beta-stats-locations">
          <span>Testing in:</span> <strong>{locations.map(placeName).join(' • ')}</strong>
        </p>
      )}

      <BetaMap locations={locations} />

      <div className="beta-stats-tiles">
        {[
          ['Dancers', stats.dancers],
          ['Fitters', stats.fitters],
          ['Shoes', stats.shoes],
          ['Snapshots', stats.snapshots],
        ]
          .filter(([, value]) => Number.isFinite(value))
          .map(([label, value]) => (
            <div className="beta-stats-tile" key={label}>
              <span className="beta-stats-number">{value.toLocaleString('en-US')}</span>
              <span className="beta-stats-label">{label}</span>
            </div>
          ))}
      </div>

      <div className="beta-stats-problems">
        <h4 className="beta-stats-problems-title">Top Problems</h4>
        {stats.topProblems.length === 0 ? (
          <p className="beta-stats-empty">Not enough data yet.</p>
        ) : (
          <ol className="beta-stats-problem-list">
            {stats.topProblems.map((problem) => (
              <li className="beta-stats-problem" key={problem.label}>
                <div className="beta-stats-problem-row">
                  <span className="beta-stats-problem-label">{problem.label}</span>
                  <span className="beta-stats-problem-count">{problem.count.toLocaleString('en-US')}</span>
                </div>
                <div className="beta-stats-bar-track">
                  <div
                    className="beta-stats-bar"
                    style={{ width: `${(problem.count / maxCount) * 100}%` }}
                  />
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      <p className="beta-stats-footnote">
        Live from the PointeSense app. Aggregate counts only
        {stats.consentOnly ? ', from participants who have signed consent.' : '.'}
      </p>
    </section>
  );
};

export default BetaStats;
